import * as Sentry from '@sentry/react'
import { logger } from './logger'

// Circuit breaker event types
export enum CircuitBreakerEvent {
  OPENED = 'circuit_breaker_opened',
  CLOSED = 'circuit_breaker_closed',
  HALF_OPENED = 'circuit_breaker_half_opened',
  REQUEST_SUCCESS = 'circuit_breaker_request_success',
  REQUEST_FAILURE = 'circuit_breaker_request_failure',
  FALLBACK_USED = 'circuit_breaker_fallback_used',
  MANUAL_RESET = 'circuit_breaker_manual_reset'
}

// Circuit breaker metrics data
export interface CircuitBreakerMetrics {
  name: string
  state: 'CLOSED' | 'OPEN' | 'HALF_OPEN'
  failures: number
  successes: number
  totalRequests: number
  lastFailureTime: number
  lastSuccessTime: number
  openedAt?: number
  closedAt?: number
  halfOpenedAt?: number
  fallbackCount: number
  manualResetCount: number
  averageResponseTime: number
  failureRate: number
  uptimePercentage: number
}

// Time series data for analytics
export interface CircuitBreakerTimeSeries {
  timestamp: number
  event: CircuitBreakerEvent
  name: string
  metadata?: Record<string, any>
}

class CircuitBreakerAnalytics {
  private metrics: Map<string, CircuitBreakerMetrics> = new Map()
  private timeSeries: CircuitBreakerTimeSeries[] = []
  private readonly maxTimeSeriesEntries = 1000
  private readonly alertThresholds = {
    failureRate: 0.1, // 10% failure rate triggers alert
    openDuration: 300000, // 5 minutes open triggers alert
    frequentOpening: 5 // Opening 5 times in 1 hour triggers alert
  }

  // Initialize metrics for a circuit breaker
  initializeMetrics(name: string): CircuitBreakerMetrics {
    const metrics: CircuitBreakerMetrics = {
      name,
      state: 'CLOSED',
      failures: 0,
      successes: 0,
      totalRequests: 0,
      lastFailureTime: 0,
      lastSuccessTime: 0,
      fallbackCount: 0,
      manualResetCount: 0,
      averageResponseTime: 0,
      failureRate: 0,
      uptimePercentage: 100
    }
    
    this.metrics.set(name, metrics)
    return metrics
  }

  // Record an event
  recordEvent(name: string, event: CircuitBreakerEvent, metadata?: Record<string, any>) {
    const metrics = this.metrics.get(name) || this.initializeMetrics(name)
    const now = Date.now()

    // Add to time series
    this.addTimeSeriesEntry({ timestamp: now, event, name, metadata })

    // Update metrics based on event type
    switch (event) {
      case CircuitBreakerEvent.OPENED:
        metrics.state = 'OPEN'
        metrics.openedAt = now
        this.checkOpenDurationAlert(name, metrics)
        this.checkFrequentOpeningAlert(name)
        break

      case CircuitBreakerEvent.CLOSED:
        metrics.state = 'CLOSED'
        metrics.closedAt = now
        break

      case CircuitBreakerEvent.HALF_OPENED:
        metrics.state = 'HALF_OPEN'
        metrics.halfOpenedAt = now
        break

      case CircuitBreakerEvent.REQUEST_SUCCESS:
        metrics.successes++
        metrics.totalRequests++
        metrics.lastSuccessTime = now
        if (metadata?.responseTime) {
          this.updateAverageResponseTime(metrics, metadata.responseTime)
        }
        break

      case CircuitBreakerEvent.REQUEST_FAILURE:
        metrics.failures++
        metrics.totalRequests++
        metrics.lastFailureTime = now
        break

      case CircuitBreakerEvent.FALLBACK_USED:
        metrics.fallbackCount++
        break

      case CircuitBreakerEvent.MANUAL_RESET:
        metrics.manualResetCount++
        break
    }

    // Update calculated metrics
    this.updateCalculatedMetrics(metrics)

    // Send to Sentry for monitoring
    this.sendToSentry(name, event, metrics, metadata)

    // Check for alerts
    this.checkAlerts(name, metrics)
  }

  private addTimeSeriesEntry(entry: CircuitBreakerTimeSeries) {
    this.timeSeries.push(entry)
    
    // Keep only the most recent entries
    if (this.timeSeries.length > this.maxTimeSeriesEntries) {
      this.timeSeries = this.timeSeries.slice(-this.maxTimeSeriesEntries)
    }
  }

  private updateAverageResponseTime(metrics: CircuitBreakerMetrics, responseTime: number) {
    const totalTime = metrics.averageResponseTime * (metrics.successes - 1) + responseTime
    metrics.averageResponseTime = totalTime / metrics.successes
  }

  private updateCalculatedMetrics(metrics: CircuitBreakerMetrics) {
    // Calculate failure rate
    if (metrics.totalRequests > 0) {
      metrics.failureRate = metrics.failures / metrics.totalRequests
    }

    // Calculate uptime percentage (rough estimate based on state changes)
    const now = Date.now()
    const dayMs = 24 * 60 * 60 * 1000
    const recentEntries = this.timeSeries.filter(
      entry => entry.name === metrics.name && entry.timestamp > now - dayMs
    )

    if (recentEntries.length > 0) {
      const openTime = recentEntries
        .filter(entry => entry.event === CircuitBreakerEvent.OPENED)
        .reduce((total, entry) => {
          const closeEntry = recentEntries.find(
            e => e.event === CircuitBreakerEvent.CLOSED && e.timestamp > entry.timestamp
          )
          const endTime = closeEntry ? closeEntry.timestamp : now
          return total + (endTime - entry.timestamp)
        }, 0)

      metrics.uptimePercentage = Math.max(0, ((dayMs - openTime) / dayMs) * 100)
    }
  }

  private sendToSentry(
    name: string, 
    event: CircuitBreakerEvent, 
    metrics: CircuitBreakerMetrics, 
    metadata?: Record<string, any>
  ) {
    // Add breadcrumb for all events
    Sentry.addBreadcrumb({
      message: `Circuit breaker ${event}`,
      category: 'circuit_breaker',
      level: event.includes('failure') || event.includes('opened') ? 'warning' : 'info',
      data: {
        name,
        event,
        state: metrics.state,
        failures: metrics.failures,
        failureRate: metrics.failureRate,
        ...metadata
      }
    })

    // Send critical events as messages/errors
    if (event === CircuitBreakerEvent.OPENED) {
      Sentry.captureMessage(`Circuit breaker opened: ${name}`, {
        level: 'warning',
        contexts: {
          circuitBreaker: {
            name,
            failures: metrics.failures,
            failureRate: metrics.failureRate,
            lastFailureTime: new Date(metrics.lastFailureTime).toISOString(),
            openedAt: new Date(metrics.openedAt!).toISOString()
          }
        },
        tags: {
          circuitBreakerName: name,
          circuitBreakerState: 'OPEN'
        }
      })
    }

    // Track custom metrics in Sentry
    Sentry.setContext('circuitBreakerMetrics', {
      [name]: {
        state: metrics.state,
        failureRate: metrics.failureRate,
        uptimePercentage: metrics.uptimePercentage,
        averageResponseTime: metrics.averageResponseTime,
        totalRequests: metrics.totalRequests
      }
    })
  }

  private checkAlerts(name: string, metrics: CircuitBreakerMetrics) {
    // High failure rate alert
    if (metrics.failureRate > this.alertThresholds.failureRate && metrics.totalRequests >= 10) {
      this.sendAlert('high_failure_rate', name, {
        failureRate: metrics.failureRate,
        threshold: this.alertThresholds.failureRate,
        totalRequests: metrics.totalRequests
      })
    }
  }

  private checkOpenDurationAlert(name: string, metrics: CircuitBreakerMetrics) {
    if (metrics.openedAt) {
      setTimeout(() => {
        const currentMetrics = this.metrics.get(name)
        if (currentMetrics?.state === 'OPEN' && currentMetrics.openedAt === metrics.openedAt) {
          this.sendAlert('circuit_open_too_long', name, {
            duration: Date.now() - metrics.openedAt!,
            threshold: this.alertThresholds.openDuration
          })
        }
      }, this.alertThresholds.openDuration)
    }
  }

  private checkFrequentOpeningAlert(name: string) {
    const oneHourAgo = Date.now() - 60 * 60 * 1000
    const recentOpenings = this.timeSeries.filter(
      entry => entry.name === name && 
               entry.event === CircuitBreakerEvent.OPENED && 
               entry.timestamp > oneHourAgo
    )

    if (recentOpenings.length >= this.alertThresholds.frequentOpening) {
      this.sendAlert('frequent_circuit_opening', name, {
        openingCount: recentOpenings.length,
        threshold: this.alertThresholds.frequentOpening,
        timeWindow: '1 hour'
      })
    }
  }

  private sendAlert(alertType: string, circuitName: string, data: Record<string, any>) {
    logger.error(`Circuit breaker alert: ${alertType}`, { circuitName, ...data })
    
    Sentry.captureMessage(`Circuit Breaker Alert: ${alertType}`, {
      level: 'error',
      contexts: {
        alert: {
          type: alertType,
          circuitName,
          ...data
        }
      },
      tags: {
        alertType,
        circuitBreakerName: circuitName
      }
    })
  }

  // Public API methods
  getMetrics(name: string): CircuitBreakerMetrics | undefined {
    return this.metrics.get(name)
  }

  getAllMetrics(): Record<string, CircuitBreakerMetrics> {
    return Object.fromEntries(this.metrics.entries())
  }

  getTimeSeries(name?: string, since?: number): CircuitBreakerTimeSeries[] {
    let filtered = this.timeSeries
    
    if (name) {
      filtered = filtered.filter(entry => entry.name === name)
    }
    
    if (since) {
      filtered = filtered.filter(entry => entry.timestamp >= since)
    }
    
    return filtered
  }

  getHealthReport(): {
    overall: 'healthy' | 'warning' | 'critical'
    circuits: Array<{
      name: string
      status: 'healthy' | 'warning' | 'critical'
      metrics: CircuitBreakerMetrics
      issues: string[]
    }>
  } {
    const circuits = Array.from(this.metrics.entries()).map(([name, metrics]) => {
      const issues: string[] = []
      let status: 'healthy' | 'warning' | 'critical' = 'healthy'

      // Check for issues
      if (metrics.state === 'OPEN') {
        issues.push('Circuit is currently open')
        status = 'critical'
      } else if (metrics.state === 'HALF_OPEN') {
        issues.push('Circuit is in recovery mode')
        status = 'warning'
      }

      if (metrics.failureRate > this.alertThresholds.failureRate) {
        issues.push(`High failure rate: ${(metrics.failureRate * 100).toFixed(1)}%`)
        if (status === 'healthy') status = 'warning'
      }

      if (metrics.uptimePercentage < 99.5) {
        issues.push(`Low uptime: ${metrics.uptimePercentage.toFixed(1)}%`)
        if (status === 'healthy') status = 'warning'
      }

      return { name, status, metrics, issues }
    })

    // Determine overall health
    const criticalCount = circuits.filter(c => c.status === 'critical').length
    const warningCount = circuits.filter(c => c.status === 'warning').length
    
    let overall: 'healthy' | 'warning' | 'critical' = 'healthy'
    if (criticalCount > 0) {
      overall = 'critical'
    } else if (warningCount > 0) {
      overall = 'warning'
    }

    return { overall, circuits }
  }

  // Export data for analytics
  exportData(format: 'json' | 'csv' = 'json') {
    if (format === 'json') {
      return {
        metrics: this.getAllMetrics(),
        timeSeries: this.timeSeries,
        healthReport: this.getHealthReport(),
        exportedAt: new Date().toISOString()
      }
    }

    if (format === 'csv') {
      // Convert time series to CSV format
      const csvHeaders = ['timestamp', 'event', 'name', 'metadata']
      const csvRows = this.timeSeries.map(entry => [
        new Date(entry.timestamp).toISOString(),
        entry.event,
        entry.name,
        JSON.stringify(entry.metadata || {})
      ])

      return [csvHeaders, ...csvRows]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n')
    }

    return null
  }

  // Clear old data
  cleanup(maxAge: number = 7 * 24 * 60 * 60 * 1000) { // Default 7 days
    const cutoff = Date.now() - maxAge
    this.timeSeries = this.timeSeries.filter(entry => entry.timestamp >= cutoff)
  }
}

// Global analytics instance
export const circuitBreakerAnalytics = new CircuitBreakerAnalytics()

// Export types and enums
export { CircuitBreakerAnalytics }